const REMOVE_KEYS_ATTRIBUTE = 'data-gift-remove-keys';

class CartGiftChooser extends HTMLElement {
  constructor() {
    super();
    this.isRemoving = false;
    this._lockedTiles = [];
  }

  // Idiomorph reuses the live node, so connectedCallback does not re-fire after a
  // repaint — an observed attribute is the only signal that covers both the first
  // upgrade and every subsequent morph.
  static get observedAttributes() {
    return [REMOVE_KEYS_ATTRIBUTE];
  }

  connectedCallback() {
    this.addEventListener('click', (event) => {
      // Read before the lock disables the tile: only an addable set tile carries items.
      const setTile = event.target.closest('[data-gift-set-items]');
      this.lockTiles(event);
      if (setTile) this.claimSet(setTile);
    });

    this.handleMutationEnd = (event) => this.unlockTiles(event.detail?.ok === true);
    document.addEventListener('cart:mutation-end', this.handleMutationEnd);

    // A chooser the morph inserts arrives already upgraded, so its attribute callback
    // ran while detached (no drawer to call) — e.g. a gift added to an empty cart.
    this.removeGifts(this.getAttribute(REMOVE_KEYS_ATTRIBUTE));
  }

  disconnectedCallback() {
    document.removeEventListener('cart:mutation-end', this.handleMutationEnd);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== REMOVE_KEYS_ATTRIBUTE) return;

    // Equal values mean the last removal changed nothing — the guard that stops a
    // failed update from retrying forever. Detached: connectedCallback handles it.
    if (newValue === oldValue || !this.isConnected) return;

    this.removeGifts(newValue);
  }

  // `key~quantity` pairs, not bare keys: the allowance is counted in units, so a
  // line holding several gifts is trimmed to its target rather than zeroed.
  parseUpdates(value) {
    const updates = {};
    for (const spec of (value ?? '').split(',').filter(Boolean)) {
      const separator = spec.lastIndexOf('~');
      if (separator === -1) continue;

      const quantity = Number.parseInt(spec.slice(separator + 1), 10);
      if (Number.isNaN(quantity)) continue;

      updates[spec.slice(0, separator)] = quantity;
    }
    return updates;
  }

  // A gift set is its products, at their set quantities, added in one request. When another set is already
  // claimed, its lines are zeroed first so the swap never leaves both sets in the
  // cart; only the add carries the sections the repaint needs.
  claimSet(tile) {
    const host = this.closest('cart-drawer');
    if (!host) return;

    let items;
    try {
      ({ items } = JSON.parse(tile.dataset.giftSetItems));
    } catch {
      return;
    }

    const swapUpdates = this.parseUpdates(tile.dataset.giftSwapKeys);

    host.mutate(
      async (sections) => {
        if (Object.keys(swapUpdates).length) await CartAPI.update({ updates: swapUpdates });
        return CartAPI.addMultiple(items, sections);
      },
      {
        control: tile,
        announce: host.announcement('announceAdded', { '%product%': tile.dataset.productTitle }),
      },
    );
  }

  removeGifts(newValue) {
    if (!newValue || this.isRemoving) return;

    const host = this.closest('cart-drawer');
    if (!host) return;

    const updates = this.parseUpdates(newValue);

    if (!Object.keys(updates).length) return;

    this.isRemoving = true;

    // Read now: the repaint after the removal drops the attribute with the keys.
    const message = this.dataset.giftRemoveMessage;

    // Deferred out of the morph's synchronous frame so a mutation is never issued
    // from inside Idiomorph's own DOM write.
    requestAnimationFrame(() => {
      host
        // `this` is the [data-error-scope] element, so a failure lands in the gift slot.
        .mutate((sections) => CartAPI.update({ updates }, sections), {
          control: this,
          announce: message || host.announcement('announceGiftRemoved'),
        })
        .then((result) => {
          if (result?.ok) this.showNotice(message);
        })
        .finally(() => {
          this.isRemoving = false;
        });
    });
  }

  // Says why a claimed set left the cart. The notice is server-rendered hidden, so
  // the next repaint clears it once the cart changes again.
  showNotice(message) {
    const notice = this.querySelector('[data-gift-notice]');
    if (!notice || !message) return;

    notice.textContent = message;
    notice.hidden = false;
  }

  // Fires before the drawer's delegated add handler, so two rapid taps cannot both
  // reach /cart/add.js and overshoot the allowance. Every tile is locked, the clicked
  // one included: the add no longer blocks its own control — its result is painted
  // optimistically — so the allowance guard belongs here, in the component that owns
  // the allowance, rather than riding on a spinner meant to cover latency. Disabling
  // the clicked tile mid-dispatch does not stop this click reaching the drawer; it
  // only stops the next one. Only tiles this lock actually changed are tracked, so a
  // tile Liquid already rendered disabled is never wrongly re-enabled.
  lockTiles(event) {
    if (!event.target.closest('[data-gift-tile]')) return;

    this._lockedTiles = [...this.querySelectorAll('[data-gift-tile]')].filter((tile) => !tile.disabled);

    for (const tile of this._lockedTiles) tile.disabled = true;
  }

  // On success the repaint re-renders every tile's true state — including the clicked
  // one, whose lock Idiomorph drops when the incoming markup has no `disabled` — so
  // the lock is simply forgotten. A failure never morphs, so the tiles must be
  // restored explicitly or they stay disabled for the rest of the session with no
  // visible cause.
  unlockTiles(didSucceed) {
    if (!didSucceed) {
      for (const tile of this._lockedTiles) {
        if (tile.isConnected) tile.disabled = false;
      }
    }

    this._lockedTiles = [];
  }
}

if (!customElements.get('cart-gift-chooser')) {
  customElements.define('cart-gift-chooser', CartGiftChooser);
}
